import React, { useEffect, useMemo, useState, useRef, useCallback } from "react"
import { css } from "@emotion/react"

import {
	CustomerSite,
	useAuth,
	useSites,
	useIsUser,
	UserId,
	useUserProfile,
	UserMasterShipTo,
} from "@ncs/ncs-api"
import {
	AddressFormModalEcomm,
	AnimatedEntrance,
	Box,
	ButtonEcomm,
	GenericAddress,
	getAddressFields,
	getGenericAddress,
	Paragraph,
	ParagraphList,
	PhoneInputEcomm,
	SearchableSiteSelectorEcomm,
	ThrottledTextarea,
	UspsAddress,
	TrackingEvent,
	Icon,
} from "@ncs/web-legos"

import { useShopContext } from "~/contexts"
import {
	addressHelpTextStyle,
	addressTextStyle,
	addAddressButtonTextStyle,
	addressContainerStyle,
	addAddressButtonContainerStyle,
	addAddressButtonStyle,
	additionalCommentsStyle,
	changeAddressTextStyle,
} from "./style"
import { useAnalytics } from "~/hooks/useAnalytics"

interface ChooseShippingAddressEcommProps {
	updateComment: (newValue: string | null) => void
}

export const ChooseShippingAddressEcomm: React.FC<ChooseShippingAddressEcommProps> = ({
	updateComment,
}) => {
	const { user } = useAuth()
	const [profile] = useUserProfile(user?.id)
	const auth = useAuth()
	const isDb = useIsUser(UserId.DrivenBrands)
	const isMr = useIsUser(UserId.MisterCarwash)
	const isSplash = useIsUser(UserId.SplashCarwash)
	const isGuest = profile?.isGuest
	const isPunchout = !!auth.punchOutSessionId
	const isPunchoutSitesDisabled = isPunchout && (isMr || isSplash)
	const [sites, sitesLoading] = useSites()
	const [{ checkout }, shopDispatch] = useShopContext()
	const [showAlternateAddressForm, setShowAlternateAddressForm] = useState(false)
	const [selectedSite, setSelectedSite] = useState<CustomerSite | null>(null)
	const [alternateAddress, setAlternateAddress] = useState<UspsAddress | null>(null)
	const [phoneNumber, setPhoneNumber] = useState<string | null>(null)
	const hasAutoSelectedShipping = useRef(false)
	const { trackEvent } = useAnalytics()

	const hasSites = useMemo(() => {
		return !!sites && sites.length > 0
	}, [sites])

	useEffect(() => {
		if (isPunchoutSitesDisabled && sites && sites.length > 0) {
			onSiteSelect(sites[0])
		}
	}, [isPunchoutSitesDisabled, sites])

	useEffect(() => {
		if (hasAutoSelectedShipping.current) return

		if (isMr) {
			shopDispatch({
				type: "select shipping site",
				payload: {
					siteId: UserMasterShipTo.MisterCarwash,
				},
			})
			hasAutoSelectedShipping.current = true
		} else if (
			!sitesLoading &&
			sites?.length === 1 &&
			!checkout.shipToSiteId &&
			!checkout.alternateAddressId &&
			!checkout.alternateAddress
		) {
			const site = sites[0]
			if (!site?.phone) {
				// eslint-disable-next-line no-console
				console.log(
					"this site does not have a phone number, please include one for your order"
				)
			}

			if (typeof isGuest === "boolean") {
				if (isGuest) {
					shopDispatch({
						type: "select shipping site",
						payload: {
							alternateAddressId: site.id,
							isGuest: isGuest,
						},
					})
				} else {
					shopDispatch({
						type: "select shipping site",
						payload: {
							siteId: site.id,
							isGuest: isGuest,
						},
					})
				}
				hasAutoSelectedShipping.current = true
			}
		}
	}, [
		checkout.shipToSiteId,
		checkout.alternateAddressId,
		checkout.alternateAddress,
		sitesLoading,
		shopDispatch,
		sites,
		isGuest,
		isMr,
	])

	useEffect(() => {
		// may need to update how this sets the address.
		if (alternateAddress) {
			shopDispatch({
				type: "select alternate address",
				payload: {
					alternateAddress: getGenericAddress(alternateAddress),
					preserveShipToId: !(isGuest && alternateAddress),
				},
			})
		}

		if (phoneNumber) {
			shopDispatch({
				type: "set checkout phone",
				payload: phoneNumber,
			})
		}
	}, [alternateAddress, phoneNumber, shopDispatch, isGuest])

	const selectedAddress: GenericAddress | null = useMemo(() => {
		if (checkout.alternateAddress) {
			return checkout.alternateAddress
		} else if (checkout.shipToSiteId || checkout.alternateAddressId) {
			const site = (sites ?? []).find(
				(s) => s.id === checkout.shipToSiteId || s.id === checkout.alternateAddressId
			)

			if (!site && !sitesLoading) {
				// eslint-disable-next-line no-console
				console.warn(
					"The site ID in checkout state does not match any of the sites coming back this customer."
				)
			}

			if (!site?.phone) {
				// eslint-disable-next-line no-console
				console.log(
					"this site does not have a phone number, please include one for your order"
				)
			}

			if (site) {
				return getGenericAddress(site)
			}
		}
		return null
	}, [
		checkout.shipToSiteId,
		checkout.alternateAddress,
		sites,
		sitesLoading,
		checkout.alternateAddressId,
	])

	const onSiteSelect = useCallback(
		(site: CustomerSite | null) => {
			setAlternateAddress(null)
			if (typeof isGuest === "boolean") {
				setSelectedSite(site)
				if (site && !isGuest) {
					shopDispatch({
						type: "select shipping site",
						payload: {
							siteId: site.id,
							isGuest,
						},
					})
				} else {
					shopDispatch({
						type: "select shipping site",
						payload: {
							alternateAddressId: site?.id,
							isGuest,
						},
					})
				}
			}
		},
		[isGuest, shopDispatch]
	)

	const onAlternateAddressSave = (address: UspsAddress, phone: string) => {
		setShowAlternateAddressForm(false)
		setSelectedSite(null)
		setAlternateAddress(address)
		setPhoneNumber(phone)
	}

	const changeAddressHandler = () => {
		setShowAlternateAddressForm(true)
		trackEvent(TrackingEvent.SHIPMENT)
	}

	return (
		<>
			<Paragraph css={addressHelpTextStyle} mb={0.5} mt={1}>
				{profile?.isGuest || !hasSites ?
					"Add an address below to ship your order."
				:	"Ship to your NCS-registered site."}
			</Paragraph>
			{isPunchoutSitesDisabled && (
				<div
					css={css`
						display: flex;
						width: 648px;
						height: 42px;
						padding: 8px 12px;
						flex-direction: row;
						align-items: flex-start;
						gap: 10px;
						flex-shrink: 0;
						border-radius: 8px;
						border: 1px solid var(--Semantic-Yellow-400, #fbbf24);
						background: #fffbeb;
					`}
				>
					<Icon icon="triangle-exclamation" customColor="#FBBF24" />
					Select Shipping Address in your ERP system after submitting your order.
				</div>
			)}
			{hasSites && !isPunchoutSitesDisabled ?
				<SearchableSiteSelectorEcomm
					initialId={checkout.alternateAddressId ?? checkout.shipToSiteId}
					value={selectedSite}
					onChange={onSiteSelect}
					mb={selectedAddress ? 1 : 0.5}
					disabled={isMr || isSplash}
				/>
			:	null}

			{selectedAddress && !isPunchoutSitesDisabled ?
				<Box d="flex" css={addressContainerStyle} justifyContent="space-between">
					<Box>
						<Paragraph css={addressTextStyle}>Address</Paragraph>
						<div>
							<ParagraphList
								lines={getAddressFields(selectedAddress, undefined, phoneNumber)}
							/>
						</div>
					</Box>
					{(profile?.isGuest || !hasSites) && (
						<Box>
							<ButtonEcomm
								disabled={isMr}
								css={css`
									margin-top: 0;
								`}
								variant="text"
								onClick={changeAddressHandler}
							>
								<span css={changeAddressTextStyle}>Change Address</span>
							</ButtonEcomm>
						</Box>
					)}
				</Box>
			:	!isPunchout && (
					<Box css={addAddressButtonContainerStyle}>
						{!alternateAddress && (profile?.isGuest || !hasSites) && (
							<ButtonEcomm
								css={addAddressButtonStyle}
								variant="secondary-cta"
								onClick={changeAddressHandler}
								containerProps={{
									mt: 0.5,
								}}
							>
								<span css={addAddressButtonTextStyle}>Add Address</span>
							</ButtonEcomm>
						)}
					</Box>
				)
			}

			{(isPunchoutSitesDisabled || selectedAddress) && (
				<AnimatedEntrance
					show={!!selectedAddress || isPunchoutSitesDisabled}
					d="flex"
					mt={1}
					gap={1}
				>
					<PhoneInputEcomm
						required={!isDb}
						value={checkout.phone}
						onChange={(value) => {
							shopDispatch({
								type: "set checkout phone",
								payload: value,
							})
						}}
						fillContainer={false}
						label={
							isDb ? "Delivery contact phone" : "Delivery contact phone (required)"
						}
						width={18}
						mb={0.5}
					/>
				</AnimatedEntrance>
			)}

			{(isPunchoutSitesDisabled || !isPunchout) && (
				<Box mt={1}>
					<ThrottledTextarea
						css={additionalCommentsStyle}
						value={checkout.comment}
						onChange={updateComment}
						label="Additional Comments"
						placeholder="Any additional comments about your order..."
					/>
				</Box>
			)}

			<AddressFormModalEcomm
				isOpen={showAlternateAddressForm}
				onClose={() => setShowAlternateAddressForm(false)}
				onSave={onAlternateAddressSave}
				hardCodedFields={
					user?.name ?
						{
							name: user.name,
						}
					:	undefined
				}
				allowCanada={hasSites}
				trackEvent={trackEvent}
			/>
		</>
	)
}
